// Geocoding utilities for converting locationText to coordinates
// Client-side fallback (for when Cloud Function geocoding isn't available)

export interface GeoCoordinates {
  lat: number;
  lng: number;
}

/**
 * Parse coordinates from locationText if they're embedded (e.g., "40.7, -111.8" or "40.7,-111.8")
 */
export function parseCoordinatesFromText(locationText?: string): GeoCoordinates | null {
  if (!locationText || typeof locationText !== 'string') return null;
  
  // Try to extract coordinates if present (format: "lat, lng" or "lat,lng")
  const coordMatch = locationText.match(/(-?\d+\.?\d*)[,\s]+(-?\d+\.?\d*)/);
  if (coordMatch) {
    const lat = parseFloat(coordMatch[1]);
    const lng = parseFloat(coordMatch[2]);
    if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      return { lat, lng };
    }
  }
  
  return null;
}

/**
 * Client-side geocoding using Google Maps Geocoding API (requires API key)
 * Note: This should ideally be done server-side via Cloud Function to avoid exposing API key
 * This is a fallback for when server-side geocoding isn't available
 */
export async function geocodeLocationText(
  locationText: string,
  apiKey?: string
): Promise<GeoCoordinates | null> {
  if (!locationText || !apiKey) return null;

  try {
    // First try parsing as coordinates
    const coords = parseCoordinatesFromText(locationText);
    if (coords) return coords;

    // Use Google Geocoding API (client-side - not ideal but works as fallback)
    const encodedLocation = encodeURIComponent(locationText);
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodedLocation}&key=${apiKey}`;
    
    const response = await fetch(url);
    if (!response.ok) {
      console.warn('[geocodeLocationText] Geocoding API request failed:', response.status);
      return null;
    }

    const data = await response.json();
    if (data.status === 'OK' && data.results && data.results.length > 0) {
      const location = data.results[0].geometry.location;
      return {
        lat: location.lat,
        lng: location.lng,
      };
    }

    console.warn('[geocodeLocationText] Geocoding failed:', data.status, data.error_message);
    return null;
  } catch (err) {
    console.error('[geocodeLocationText] Error:', err);
    return null;
  }
}

/**
 * Check if a profile has valid coordinates (location.lat/lng or legacy geo.lat/lng).
 */
export function hasValidGeo(profile: {
  geo?: { lat?: number; lng?: number };
  location?: { lat?: number; lng?: number; city?: string; state?: string } | string;
}): boolean {
  const loc = profile.location;
  if (loc && typeof loc === 'object' && typeof loc.lat === 'number' && typeof loc.lng === 'number') {
    const { lat, lng } = loc;
    if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      return true;
    }
  }
  if (!profile.geo) return false;
  const { lat, lng } = profile.geo;
  return (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    !isNaN(lat) &&
    !isNaN(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}
